import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { COMMON_TIMEZONES, userBrowserTimezone } from "@/lib/time";
import ContactAutocomplete from "@/components/ContactAutocomplete";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

/**
 * Create a meeting — date + start time + end time (duration auto-calculated)
 * + the same Recall + access toggles you can edit later on the meeting
 * settings tab. Keeps the meeting model in sync with the editor so users see
 * the same controls in both places.
 */
export default function CreateMeeting() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const today = new Date();
  const padded = (n) => String(n).padStart(2, "0");
  const [date, setDate] = useState(
    `${today.getFullYear()}-${padded(today.getMonth() + 1)}-${padded(today.getDate())}`
  );
  // Start time defaults to next top-of-the-hour.
  const [time, setTime] = useState(`${padded(today.getHours() + 1)}:00`);
  // End time defaults to start + 30 min — computed in the effect below so the
  // two are always consistent on first mount.
  const [endTime, setEndTime] = useState("");
  const [duration, setDuration] = useState(30);
  // `dirty` marks whether the user manually edited a particular field — when
  // they have, the auto-calc effects respect their choice instead of
  // overwriting it. (See _recalc functions below.)
  const [tz, setTz] = useState(user?.timezone || userBrowserTimezone());
  const [guestAccess, setGuestAccess] = useState(true);
  const [waitingRoom, setWaitingRoom] = useState(
    currentWorkspace?.scheduled_meeting_waiting_room_default ?? true
  );
  const [aiSummary, setAiSummary] = useState(true);
  const [autoSummaryOnEnd, setAutoSummaryOnEnd] = useState(true);
  const [transcription, setTranscription] = useState(false);
  const [autoTranscriptionStart, setAutoTranscriptionStart] = useState(false);
  const [recording, setRecording] = useState(false);
  const [invites, setInvites] = useState("");
  // Evergreen / persistent room — when checked, the meeting becomes a
  // permanent room (no fixed start time effectively) with optional custom
  // slug. The room snaps back to "scheduled" instead of ending so anyone
  // with the link can rejoin freely.
  const [isPersistent, setIsPersistent] = useState(false);
  const [customSlug, setCustomSlug] = useState("");
  const [slugError, setSlugError] = useState("");
  const [busy, setBusy] = useState(false);
  const [timeError, setTimeError] = useState("");

  // Initialise endTime once both date+time are valid (first paint).
  useEffect(() => {
    if (endTime) return;
    const s = parseDateTime(date, time);
    if (!s) return;
    const e = new Date(s.getTime() + duration * 60_000);
    setEndTime(formatTime(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply the workspace's scheduled meeting defaults. Keyed on the workspace
  // id so it runs once the workspace loads (covers async load) and re-applies
  // when switching workspaces. It won't clobber a manual per-meeting toggle
  // because the id doesn't change between renders of the same workspace.
  useEffect(() => {
    if (currentWorkspace) {
      setWaitingRoom(currentWorkspace.scheduled_meeting_waiting_room_default ?? true);
      setGuestAccess(currentWorkspace.scheduled_meeting_guest_access_default ?? true);
      setAiSummary(currentWorkspace.scheduled_meeting_ai_summary_default ?? true);
      setAutoSummaryOnEnd(currentWorkspace.scheduled_meeting_auto_summary_on_end_default ?? true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.workspace_id]);


  // Validate every time start/end change. If the end is before/equal start
  // we surface a non-blocking error and disable submit.
  useEffect(() => {
    const s = parseDateTime(date, time);
    const e = parseDateTime(date, endTime);
    if (!s || !e) { setTimeError(""); return; }
    if (e.getTime() <= s.getTime()) {
      setTimeError("End time must be after the start time.");
    } else {
      setTimeError("");
    }
  }, [date, time, endTime]);

  // Auto-recalc end-time when start-time changes (keeps the same duration).
  const onTimeChange = (next) => {
    setTime(next);
    const s = parseDateTime(date, next);
    if (!s) return;
    const e = new Date(s.getTime() + duration * 60_000);
    setEndTime(formatTime(e));
  };
  // Auto-recalc duration when end-time changes.
  const onEndTimeChange = (next) => {
    setEndTime(next);
    const s = parseDateTime(date, time);
    const e = parseDateTime(date, next);
    if (!s || !e) return;
    const mins = Math.round((e.getTime() - s.getTime()) / 60_000);
    if (mins > 0) setDuration(mins);
  };
  // Auto-recalc end-time when duration changes.
  const onDurationChange = (mins) => {
    const n = Number(mins) || 0;
    setDuration(n);
    const s = parseDateTime(date, time);
    if (!s) return;
    const e = new Date(s.getTime() + n * 60_000);
    setEndTime(formatTime(e));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!currentWorkspace) {
      toast.error("Pick a workspace first");
      return;
    }
    if (!title.trim()) {
      toast.error("Give your meeting a title");
      return;
    }
    if (timeError) {
      toast.error(timeError);
      return;
    }
    setBusy(true);
    const startDt = parseDateTime(date, time);
    if (!startDt) {
      toast.error("Invalid date/time");
      setBusy(false);
      return;
    }
    const start_time = startDt.toISOString();
    const invited_emails = invites
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      // Strip leading/trailing hyphens on submit — we leave them in
      // during typing (see onBlur in the slug input) but they must be
      // gone before the request hits the backend validator.
      const cleanSlug = (customSlug || "").replace(/^-+|-+$/g, "");
      const { data } = await api.post("/meetings", {
        title: title.trim(),
        description: desc.trim(),
        start_time,
        duration_minutes: Number(duration) || 30,
        timezone: tz,
        guest_access_enabled: guestAccess,
        waiting_room_enabled: waitingRoom,
        ai_summary_enabled: aiSummary,
        auto_summary_on_end: autoSummaryOnEnd,
        transcription_enabled: transcription,
        auto_transcription_start: autoTranscriptionStart,
        recording_enabled: recording,
        invited_emails,
        workspace_id: currentWorkspace.workspace_id,
        is_persistent: isPersistent,
        custom_slug: isPersistent && cleanSlug ? cleanSlug : undefined,
      });
      toast.success("Meeting scheduled — link ready to share");
      navigate(`/app/meetings/${data.meeting_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail.toLowerCase().includes("slug")) {
        setSlugError(detail);
        toast.error(detail);
      } else {
        toast.error(detail || "Could not create meeting");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="create-meeting-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm text-[#4a4a52] hover:text-[#005e7a] mb-4"
        data-testid="create-meeting-back"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <p className="bloq-eyebrow">Create meeting</p>
      <h1 className="bloq-headline text-4xl sm:text-5xl mt-2 mb-7">
        Create a room, capture the outcome, keep the work moving.
      </h1>
      <form onSubmit={onSubmit} className="bloq-card p-6 flex flex-col gap-5">
        <Field label="Title">
          <input className="bloq-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Discovery — Acme Corp" data-testid="meeting-title-input" />
        </Field>
        <Field label="Description (optional)">
          <textarea rows={3} className="bloq-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What's this meeting about?" data-testid="meeting-description-input" />
        </Field>
        <div className="grid sm:grid-cols-4 gap-4">
          <Field label="Date">
            <input type="date" className="bloq-input" value={date} onChange={(e) => setDate(e.target.value)} data-testid="meeting-date-input" />
          </Field>
          <Field label="Start time">
            <input type="time" className="bloq-input" value={time} onChange={(e) => onTimeChange(e.target.value)} data-testid="meeting-time-input" />
          </Field>
          <Field label="End time">
            <input type="time" className="bloq-input" value={endTime} onChange={(e) => onEndTimeChange(e.target.value)} data-testid="meeting-endtime-input" />
          </Field>
          <Field label="Duration (min)">
            <input
              type="number"
              min="1"
              max="1440"
              className="bloq-input"
              value={duration}
              onChange={(e) => onDurationChange(e.target.value)}
              data-testid="meeting-duration-input"
            />
          </Field>
        </div>
        {timeError && (
          <p className="text-xs text-red-600 -mt-2" data-testid="meeting-time-error">{timeError}</p>
        )}
        <Field label="Timezone">
          <Select value={tz} onValueChange={setTz}>
            <SelectTrigger data-testid="meeting-timezone-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-60">
              {COMMON_TIMEZONES.map((z) => (
                <SelectItem key={z} value={z}>{z.replace(/_/g, " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Invite attendees (comma or space separated)">
          <ContactAutocomplete
            workspaceId={currentWorkspace?.workspace_id}
            value={invites}
            // Match against the ACTIVE token only (text after the last
            // comma/semicolon) so suggestions keep working after the
            // user has added their first recipient. Without this the
            // query becomes the whole comma-soup and matches nothing.
            queryOverride={(invites.split(/[,;]+/).pop() || "").trim()}
            onChange={setInvites}
            onSelect={(c) => {
              if (!c.email) return;
              // Case-insensitive dedup against everything already in
              // the field (existing chips + the active draft).
              const existing = (invites || "")
                .split(/[\s,;]+/)
                .map((s) => s.trim().toLowerCase())
                .filter(Boolean);
              if (existing.includes(c.email.toLowerCase())) return;
              setInvites((prev) => {
                // Split preserving separators so we can replace the
                // last segment (the active draft) with the picked
                // email while leaving prior chips intact.
                const parts = (prev || "").split(/(\s*[,;]\s*)/);
                if (parts.length === 0) return `${c.email}, `;
                parts[parts.length - 1] = c.email;
                let result = parts.join("");
                if (!/[,;]\s*$/.test(result)) result += ", ";
                return result;
              });
            }}
            placeholder="alex@acme.com, ben@acme.com"
            testid="meeting-invites-input"
          />
        </Field>
        <div className="flex flex-col gap-3">
          <ToggleRow
            label="Evergreen meeting link"
            description="A permanent room URL you can re-enter anytime. Ending the room never disables the link — anyone with it can rejoin."
            checked={isPersistent}
            onCheckedChange={setIsPersistent}
            testid="toggle-persistent"
          />
          {isPersistent && (
            <div className="bloq-card p-4 -mt-1 ml-1 bg-[#fbfcfc] border-l-2 border-[#005e7a]/40" data-testid="evergreen-slug-block">
              <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium block mb-1.5">
                Custom URL (optional)
              </label>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs text-[#82828c] font-mono">/m/</span>
                <input
                  className="bloq-input text-sm flex-1 min-w-[140px]"
                  value={customSlug}
                  onChange={(e) => {
                    // While typing: lowercase + replace runs of unsafe
                    // chars with a single hyphen. We DO NOT strip
                    // leading/trailing hyphens here because that would
                    // eat the hyphen the user has just typed before they
                    // finish the next word (e.g. "office-" → "office"
                    // immediately, blocking them from ever typing
                    // "office-hours"). The trailing/leading-hyphen
                    // cleanup happens on submit instead.
                    const cleaned = (e.target.value || "")
                      .toLowerCase()
                      .replace(/[^a-z0-9-]+/g, "-");
                    setCustomSlug(cleaned);
                    setSlugError("");
                  }}
                  onBlur={() => {
                    // On blur: drop any leading/trailing hyphens so the
                    // submit value is always valid. Empty string is fine
                    // — that just means "no custom slug".
                    setCustomSlug((s) => (s || "").replace(/^-+|-+$/g, ""));
                  }}
                  placeholder="office-hours"
                  data-testid="evergreen-slug-input"
                  maxLength={40}
                />
              </div>
              <p className="text-[11px] text-[#82828c] mt-1.5">
                Lowercase letters, numbers, and hyphens only · 3–40 characters · must be unique across all Bloq meeting links.
                {!customSlug && " Leave blank to use the auto-generated URL."}
              </p>
              {slugError && (
                <p className="text-xs text-red-600 mt-1.5" data-testid="evergreen-slug-error">{slugError}</p>
              )}
            </div>
          )}
          <ToggleRow
            label="Allow guest access (no Bloq account required)"
            description="Visitors with the link can join. Turn off for invite-only meetings."
            checked={guestAccess}
            onCheckedChange={setGuestAccess}
            testid="toggle-guest-access"
          />
          <ToggleRow
            label="Enable waiting room"
            description="Participants wait until the host admits them. Lobby always comes first."
            checked={waitingRoom}
            onCheckedChange={setWaitingRoom}
            testid="toggle-waiting-room"
          />
          <ToggleRow
            label="AI summary"
            description="Eligible for AI summary generation using the transcript, chat, and tasks."
            checked={aiSummary}
            onCheckedChange={setAiSummary}
            testid="toggle-ai-summary"
          />
          <ToggleRow
            label="Auto-generate AI summary when meeting ends"
            description="When the host ends the meeting, Bloq generates the AI summary automatically."
            checked={autoSummaryOnEnd}
            onCheckedChange={setAutoSummaryOnEnd}
            testid="toggle-auto-summary-on-end"
            disabled={!aiSummary}
          />
          <ToggleRow
            label="Live transcription"
            description="Browser captions for the local microphone. Other participants need their own browsers."
            checked={transcription}
            onCheckedChange={setTranscription}
            testid="toggle-transcription"
          />
          <ToggleRow
            label="Auto-start transcription on join"
            description="When the meeting room loads, start captions automatically."
            checked={autoTranscriptionStart}
            onCheckedChange={setAutoTranscriptionStart}
            testid="toggle-auto-transcription"
            disabled={!transcription}
          />
          <ToggleRow
            label="Recording (host)"
            description="The host can record the meeting from inside the room. Recordings are saved to the meeting record."
            checked={recording}
            onCheckedChange={setRecording}
            testid="toggle-recording"
          />
        </div>
        <button type="submit" className="bloq-btn-primary mt-2" disabled={busy || !!timeError} data-testid="create-meeting-submit">
          {busy ? "Creating…" : "Create meeting"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function ToggleRow({ label, description, checked, onCheckedChange, testid, disabled }) {
  return (
    <div className={`flex items-start justify-between gap-4 p-3 rounded-lg bg-[#f5efe6] ${disabled ? "opacity-60" : ""}`}>
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-[#82828c] mt-0.5">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} data-testid={testid} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers — keep date/time math centralised so the auto-calc effects share
// the exact same parsing/formatting rules.
function parseDateTime(date, time) {
  if (!date || !time) return null;
  const dt = new Date(`${date}T${time}:00`);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}
function formatTime(dt) {
  if (!dt || Number.isNaN(dt.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}
